import { Badge, Button, DropdownButton, Table, Column, Drawer, Select, CalendarPicker } from "@appkit4/react-components";
import { SelectValue } from "@appkit4/react-components/esm/combobox/Combobox";
import { useQueryClient } from "@tanstack/react-query";
import { ConfirmationModal, LoaderWrapper, toastMessage } from "components/common/helpers";
import { FetchAnalyses, DeleteAnalysis } from "routes/administration/queries/analysis";
import { FetchClient } from "routes/administration/queries/client";
import { FetchRulesets } from "routes/administration/queries/ruleset";
import { FetchSystems } from "routes/administration/queries/system";
import { FC, useMemo, useState } from "react";
import { useCookies } from "react-cookie";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getDate, selectFilter, decodeToken } from "services/common";
import { AnalysisData, AnalysisStatus, UserLevel } from "routes/analysis/types/analysis";
import PublishForm from "./PublishForm";
import AnalysisForm from "./AnalysisForm";
import LogViewer from "./logViewer";

const AnalysisView: FC = () => {
  const { clientId } = useParams();
  const [visible, setVisible] = useState<string | null>(null);
  const [showPublish, setShowPublish] = useState<AnalysisData | undefined>();
  const [deleteVisible, setDeleteVisible] = useState<string | null>(null);
  const [filters, setFilters] = useState<{ date: undefined | Date[], status: SelectValue, system: SelectValue, bag: SelectValue }>({ date: undefined, status: [], system: [], bag: [] });
  const [showAdd, setShowAdd] = useState(false);
  const [cookie] = useCookies();

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [showConfirmation, setShowConfirmation] = useState(false);

  const { data: analyses, isPending: isAnalysisPending, error: analysesError } = FetchAnalyses(clientId);

  const { data: systems, isPending: isSystemsPending, error: systemsError } = FetchSystems(clientId);

  const { data: ruleBags, isPending: isBagsPending, error: bagsError } = FetchRulesets(clientId);

  const { data: clientData, isPending: isClientPending, error: clientError } = FetchClient(clientId);

  const { mutate: doDelete } = DeleteAnalysis();

  const filterBags = (select?: SelectValue, data?: AnalysisData) => {
    if (!data?.bag?.bagId || !data) return true;
    if (Array.isArray(select)) {
      if (select.length === 0) return true;
      return select.includes(data.bag?.bagId);
    }
    return true;
  }

  const filterByDates = (data: AnalysisData, dateRange: Date[]) => {
    if (!dateRange || dateRange.length < 2) return true;
    let extractionDate = new Date(data.extractionDate || "");
    if (isNaN(extractionDate.getTime())) return false;
    return (extractionDate >= dateRange[0] && extractionDate <= dateRange[1]);
  }

  const getRuleFilter = () => {
    let bagIds = Array.from(new Set(analyses?.map(m => m.bag?.bagId) || []));
    let bags = [];
    for (let bagId of bagIds) {
      let bag = ruleBags?.find(f => f.bagId === bagId);
      if (bag) {
        bags.push({ value: bag.bagId, label: bag.name });
      }
    }
    return bags;
  }
  const handleDelete = async (analysis: AnalysisData) => {
    if (!analysis) return;
    setDeleteVisible(null);
    await doDelete(analysis, {
      onError: () => {
        toastMessage("Deletion failed", null, "error");
      },
      onSuccess: () => {
        toastMessage("Analysis queued for deletion");
        queryClient.invalidateQueries({ queryKey: ["analyses", clientId] });
      }
    })
  }
  const checkUserLevel = (level: UserLevel) => {
    const jwtData = decodeToken(cookie["id_token"]);
    return jwtData?.userLevel && UserLevel[jwtData.userLevel as keyof typeof UserLevel] <= level;
  }
  const getClients = () => {
    let clients = [{
      clientId: "00000000-0000-0000-0000-000000000000",
      name: "Default"
    }];
    if (clientData) {
      clients.push({
        clientId: clientData.clientId,
        name: clientData.name
      });
    }
    return clients;
  }
  const resetFilters = () => {
    setFilters({ date: undefined, status: [], system: [], bag: [] });

  }
  const customizeColumn = (row: AnalysisData, field: string) => {
    switch (field) {
      case "analysisDate":
        return getDate(row.analysisDate || "");
      case "extractionDate":
        return (<>{getDate(row[field] || "")}</>);
      case "systemId":
        {
          let system = row.sapSystem;
          if (!system) return "";
          return (<>
            <b>{system?.sapNickname}</b>
            <p>{system?.sapSystemName} <span className="Appkit4-icon icon-arrow-right-small-outline" /> {system?.sapNickname} <span className="Appkit4-icon icon-arrow-right-small-outline" /> {system?.sapClient}</p>
          </>)
        }
      case "bag":
        if (!row.bag) return "";
        return (<>
          <b>{row[field]?.name}</b>
          <p>{row[field]?.description}</p>
        </>);
      case "status":
        switch (row["status"]) {
          case AnalysisStatus.Analyzing:
            return (<Badge value="Processing" type="info-outlined" />);
          case AnalysisStatus.Error:
            return (<Badge value="Error" type="danger" />);
          case AnalysisStatus.Analysed:
            return (<Badge value="Analysed" type="primary" />);
          case AnalysisStatus.Published:
            return (<Badge value="Published" type="success" />);
          case AnalysisStatus.Deleting:
            return (<Badge value="Queued for deletion" type="danger" />);
        }
        break;
      case "actions":
        return (
          <DropdownButton
            compact
            splitButton
            kind="tertiary"
            data={[
              { label: "View analysis", value: 1, disabled: (row["status"] || 0) < AnalysisStatus.Analysed },
              { label: "View analysis log", value: 2, disabled: !checkUserLevel(UserLevel.PwCAdmin) },
              { label: "Publish", value: 3, disabled: row["status"] !== 2 || !checkUserLevel(UserLevel.PwCAdmin) },
              { label: "Delete analysis", value: 4, disabled: !checkUserLevel(UserLevel.PwCAdmin) }
            ]}
            onSelect={(value) => {
              switch (value) {
                case 1:
                  navigate(`/analysis/${row["analysisId"]}`);
                  break;
                case 2:
                  setVisible(row["analysisId"] || null);
                  break;
                case 3:
                  setShowPublish(row);
                  break;
                case 4:
                  setDeleteVisible(row['analysisId'] || null);
                  break;
                default:
                  break;
              }
            }}
            onClick={() => (row["status"] || 0) >= AnalysisStatus.Analysed && navigate(`/analysis/${row["analysisId"]}`)}
          >
            View
          </DropdownButton>
        );
      default:
        return row[field as keyof AnalysisData];
    }
  }
  const checkIfFilterHasValues = () => {
    return (filters.date && filters.date.length > 0) || (filters.status as string[]).length > 0 || (filters.system as string[]).length > 0 || (filters.bag as string[]).length > 0;
  }
  const filteredData = useMemo(() => {
    return (
      analyses?.filter(f => selectFilter(f, 'status', filters.status))
        .filter(f => filterByDates(f, filters.date || []))
        .filter(f => selectFilter(f, 'systemId', filters.system))
        .filter(f => filterBags(filters.bag, f))
        .map(m => ({ ...m, actions: "" })) || []
    );
  }, [analyses, filters]);

  return (
    <LoaderWrapper loading={[isAnalysisPending, isBagsPending, isSystemsPending, isClientPending]} errors={[analysesError, bagsError, systemsError, clientError]}>
      <h3>Analyses</h3>
      {
        checkUserLevel(UserLevel.PwCAdmin) && (<p>A view for creating new analyses and reviewing existing analyses.</p>)
      }
      <div className="flex items-center gap-4 mt-4 mb-4">
        {
          checkUserLevel(UserLevel.PwCAdmin) && (<div className="shrink">
            <Button kind='primary' icon="icon-plus-outline" onClick={() => setShowAdd(true)}>New analysis</Button>
          </div>)
        }
        <div className="pr-16">
          <CalendarPicker
            selectRange
            value={filters.date}
            placeholder="Filter by extraction date"
            className="list-filter"
            dateSeparator="."
            format="DD.MM.YYYY"
            viewStart="year"
            viewEnd="decade"
            onChange={(values: Date[]) => setFilters({ ...filters, date: values })}
          />
        </div>
        <div>
          <Select
            placeholder="Filter by status"
            className="list-filter"
            value={filters.status}
            multiple
            data={Array.from(new Set(analyses?.map(m => m.status)) || []).map(m => ({ value: m, label: AnalysisStatus[m as AnalysisStatus] }))}
            onSelect={(value) => setFilters({ ...filters, status: value })}
          />
        </div>
        <div>
          <Select
            placeholder="Filter by system"
            className="list-filter"
            value={filters.system}
            multiple
            data={systems?.map(m => ({ value: m.systemId, label: m.sapNickname }))}
            onSelect={(value) => setFilters({ ...filters, system: value })}
          />
        </div>
        <div>
          <Select
            placeholder="Filter by rule set"
            className="list-filter"
            value={filters.bag}
            multiple
            data={
              getRuleFilter()
            }
            onSelect={(value) => setFilters({ ...filters, bag: value })}
          />
        </div>
        {
          checkIfFilterHasValues() &&
          <div>
            <Button kind="secondary" onClick={resetFilters}>Clear</Button>
          </div>
        }
      </div>
      <LoaderWrapper loading={[isAnalysisPending]} errors={[analysesError]} inline>
        {
          filteredData.length > 0
            ? <Table
              originalData={filteredData}
              hasTitle
              striped
            >
              <Column field="analysisId" renderCell={(row) => row.status > AnalysisStatus.Error && <Link to={`/analysis/${row.analysisId}`}><span className="Appkit4-icon icon-hyperlink-fill"></span> View analysis</Link>}></Column>
              <Column field="analysisDate" sortKey="analysisDate" renderCell={customizeColumn}>Analysis date</Column>
              <Column field="extractionDate" sortKey="extractionDate" renderCell={customizeColumn}>Extraction date</Column>
              <Column field="bag" sortKey="bag" renderCell={customizeColumn}>Rule set</Column>
              <Column field="systemId" sortKey="systemId" renderCell={customizeColumn}>System</Column>
              <Column field="status" sortKey="status" renderCell={customizeColumn}>Status</Column>
              <Column field="actions" renderCell={customizeColumn}>Actions</Column>
            </Table>
            : <p>No analyses found</p>
        }
      </LoaderWrapper>
      <Drawer
        initialFocus={false}
        mask={true}
        resizable={true}
        footer={
          <>
            <Button onClick={() => setVisible(null)}>Ok</Button>
          </>
        }
        visible={visible !== null}
        placement="right"
        title="Log"
        onClose={() => setVisible(null)}
      >
        {visible && <LogViewer analysisId={visible || ""} />}
      </Drawer>
      <ConfirmationModal
        visible={deleteVisible !== null}
        title="Delete analysis"
        cancel={() => setDeleteVisible(null)}
        confirm={async () => (deleteVisible && analyses) && await handleDelete(analyses.filter(f => f.analysisId === deleteVisible)[0])}>
        <>
          <p>Are you sure you want to delete this analysis?</p>
          <p>Analysis will be queued for deletion, and will be gone within the next 24 hours.</p>
        </>
      </ConfirmationModal>
      {
        <Drawer
          initialFocus={false}
          mask={true}
          resizable={true}
          style={{ minWidth: "800px" }}
          visible={showAdd}
          placement="right"
          title="Add new analysis"
          onClose={() => setShowAdd(false)}
        >
          <AnalysisForm
            systems={systems || []}
            ruleBags={ruleBags || []}
            cancel={() => {
              setShowAdd(false);
            }}
            confirmed={() => {
              setShowAdd(false);
              setShowConfirmation(true);
            }}
            clients={getClients()}
          />
        </Drawer>
      }
      <ConfirmationModal
        visible={showConfirmation}
        title="Analysis created"
        cancel={() => {
          setShowConfirmation(false);
        }}
        confirm={() => {
          setShowConfirmation(false);
        }}
      >
        <p>Analysis has been created and is now being processed.</p>
        <p>It will appear on the list of analyses within the next 15 minutes. You can follow the progress via log view.</p>
      </ConfirmationModal>
      <PublishForm analysis={showPublish} close={() => setShowPublish(undefined)} />
    </LoaderWrapper>
  )
}



export default AnalysisView;